import type {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import { supportTicketHydrationQueryRetryOptions } from "@redotech/redo-support-v2/query/support-ticket-hydration-query";
import {
  supportTicketTagQueryKeys,
  type AccountTagsResponse,
} from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
import { useQuery } from "@tanstack/react-query";
import { catchApiErrorsSilent } from "../db/error-handling";

const EMPTY_TAGS: readonly TagResponse[] = [];
const EMPTY_TAG_GROUPS: readonly TagGroupResponse[] = [];
const FIVE_MINUTES_MS = 5 * 60 * 1000;

export interface UseAccountTagsResult {
  tags: readonly TagResponse[];
  tagGroups: readonly TagGroupResponse[];
  /** No data yet and a first fetch is in flight. */
  isLoading: boolean;
  /** A fetch (initial or background) is currently running. */
  isFetching: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

/** Single React Query cache entry for the account's tags and tag groups, read by every tag surface. */
export function useAccountTags(): UseAccountTagsResult {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const scope = useSupportTicketQueryScope();

  const query = useQuery<AccountTagsResponse>({
    queryKey: supportTicketTagQueryKeys.accountTags(scope),
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrorsSilent(client.tag.getAccountTags({})),
      );
      return { tags: [...result.tags], tagGroups: [...result.tagGroups] };
    },
    staleTime: FIVE_MINUTES_MS,
    retry: supportTicketHydrationQueryRetryOptions.retry,
    retryDelay: supportTicketHydrationQueryRetryOptions.retryDelay,
    refetchInterval: supportTicketHydrationQueryRetryOptions.refetchInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: { showErrorToast: true },
  });

  return {
    tags: query.data?.tags ?? EMPTY_TAGS,
    tagGroups: query.data?.tagGroups ?? EMPTY_TAG_GROUPS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}
